/**
 * TBB Curator Rankings — Frontend JS
 *
 * 1. Fetches top-10 vendor rankings from the CF worker
 * 2. For each vendor, fetches the Shopify collection image via /collections/{handle}.json
 * 3. Replaces skeleton cards with live data
 * 4. Wires up scroll nav arrows
 */
(function () {
  'use strict';

  var section = document.querySelector('[data-tbb-curator-section]');
  if (!section) return;

  var workerUrl = section.getAttribute('data-curator-worker-url');
  if (!workerUrl) {
    console.warn('[TBB Curators] No worker URL configured.');
    return;
  }

  var track = section.querySelector('[data-tbb-curator-track]');
  var prev = section.querySelector('.tbb-curator-prev');
  var next = section.querySelector('.tbb-curator-next');

  /* ── Border color palette (cycles through for visual variety) ── */
  var ACCENT_COLORS = ['#8de0f4', '#9c01ff', '#fe8aed', '#f5c842', '#6bff8e'];

  /* ── Helpers ── */

  function formatNumber(n) {
    if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
    return String(n);
  }

  /**
   * Build a single curator card element.
   * Uses profileImage from PV API (Cloudinary-hosted business logo).
   */
  /**
   * Safely create an element with class, text, and optional inline style.
   * Uses DOM methods only — no innerHTML — to prevent XSS from API data.
   */
  function el(tag, className, textContent, style) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (textContent) node.textContent = textContent;
    if (style) node.setAttribute('style', style);
    return node;
  }

  function buildCard(vendor, rank, accentColor) {
    var card = document.createElement('a');
    card.href = '/collections/' + encodeURIComponent(vendor.handle || '');
    card.className = 'tbb-curator-card';
    card.style.setProperty('--curator-accent', accentColor);

    // Badge
    card.appendChild(el('span', 'tbb-curator-badge', String(rank)));

    // Avatar container
    var avatarWrap = el('div', 'tbb-curator-avatar', null, 'border-color:' + accentColor + ';');
    if (vendor.profileImage) {
      var imgUrl = vendor.profileImage.replace('/upload/', '/upload/w_192,h_192,c_fill,g_face/');
      var img = document.createElement('img');
      img.src = imgUrl;
      img.alt = vendor.vendorName || '';
      img.className = 'tbb-curator-avatar-img';
      img.loading = 'lazy';
      img.width = 96;
      img.height = 96;
      avatarWrap.appendChild(img);
    } else {
      var placeholder = el('div', 'tbb-curator-avatar-placeholder',
        (vendor.vendorName || '?').charAt(0).toUpperCase());
      avatarWrap.appendChild(placeholder);
    }
    card.appendChild(avatarWrap);

    // Handle name
    card.appendChild(el('p', 'tbb-curator-handle', vendor.vendorName || ''));

    // Stats
    var statsWrap = el('div', 'tbb-curator-stats');
    statsWrap.appendChild(el('span', 'tbb-curator-stat',
      formatNumber(vendor.productsCount) + ' throwbacks listed'));
    // "Memories shipped" stat hidden per LeMarc 2026-06-29 — keep the
    // throwbacks-listed line as the sole curator stat.
    card.appendChild(statsWrap);

    return card;
  }

  /* ── Scroll nav (same pattern as carousel design system) ── */

  function getScrollAmount() {
    var card = track.querySelector('.tbb-curator-card');
    if (!card) return 200;
    return card.offsetWidth + parseInt(getComputedStyle(track).gap || '12', 10);
  }

  function updateBtns() {
    if (!prev || !next) return;
    prev.disabled = track.scrollLeft <= 2;
    next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
  }

  if (prev && next) {
    prev.addEventListener('click', function () {
      track.scrollBy({ left: -getScrollAmount() * 2, behavior: 'smooth' });
    });
    next.addEventListener('click', function () {
      track.scrollBy({ left: getScrollAmount() * 2, behavior: 'smooth' });
    });
    track.addEventListener('scroll', updateBtns, { passive: true });
  }

  /* ── Main: fetch rankings and populate ── */

  // Deadline via TBBFetch: raced against our own timer, so it still fires even
  // if whatever owns window.fetch on the storefront drops our abort signal.
  // See DEV_NOTES 2026-07-29.
  (window.TBBFetch || fetch)(workerUrl, { timeout: 5000 })
    .then(function (res) {
      if (!res.ok) throw new Error('Worker returned ' + res.status);
      return res.json();
    })
    .then(function (json) {
      if (!json.success || !json.data || json.data.length === 0) {
        console.warn('[TBB Curators] No ranking data returned.');
        return;
      }

      var vendors = json.data;

      // Clear skeleton cards
      track.innerHTML = '';

      // Build live cards (profileImage comes from worker via PV API)
      vendors.forEach(function (vendor, i) {
        var accent = ACCENT_COLORS[i % ACCENT_COLORS.length];
        var card = buildCard(vendor, i + 1, accent);
        track.appendChild(card);
      });

      // Re-init scroll nav
      updateBtns();
    })
    .catch(function (err) {
      console.error('[TBB Curators] Failed to load rankings:', err);
      // Leave skeleton cards visible as graceful fallback
    });

  /* ── Shopify theme editor support ── */
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function () {
      updateBtns();
    });
  }
})();
